import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { navigate } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { memo, useCallback, useEffect, useState } from "react";
import DotControls from "../common/DotControls";

const Hero = ({ data }) => {
  const autoplayOptions = {
    delay: 6000,
    rootNode: (emblaRoot) => emblaRoot.parentElement,
  };
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      dragFree: false,
      containScroll: "trimSnaps",
      speed: 20,
      loop: true,
    },
    [Autoplay(autoplayOptions)]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  function handleGoTo(slide) {
    emblaApi.scrollTo(slide);
  }

  return (
    <div className="relative w-full h-[585px] md:h-full box-border">
      <div
        id="featured-blogs"
        className="w-full h-full relative z-[1] overflow-visible embla"
      >
        <div className="w-full h-full overflow-hidden" ref={emblaRef}>
          <div className="flex flex-row h-full w-full">
            {data.map((slider, index) => (
              <div
                className="w-screen h-full relative border-inherit z-0 flex-grow-0 flex-shrink-0 basis-full"
                key={index}
              >
                <div
                  className="relative flex flex-wrap lg:flex-initial lg:min-h-[720px] h-full max-h-[585px] md:max-h-full text-white overflow-y-hidden
                  border-inherit"
                >
                  <div className="hidden md:block absolute inset-0 w-full h-full overflow-hidden border-inherit">
                    {slider.backgroundImage.mimeType.includes("image") ? (
                      <GatsbyImage
                        image={slider.backgroundImage.gatsbyImageData}
                        alt={slider.backgroundImage.title}
                        className="max-w-full w-full h-full object-cover align-bottom"
                        objectFit="cover"
                        objectPosition="center"
                        loading="eager"
                        quality={100}
                        webpOptions={{
                          lossless: true
                        }}
                      />
                    ) : (
                      <video
                        title={slider.backgroundImage.title}
                        src={slider.backgroundImage.file.url}
                        autoPlay
                        loop
                        muted
                        controlsList="nodownload"
                        className="w-full h-full object-cover"
                      >
                        <source
                          src={slider.backgroundImage.file.url}
                          type={slider.backgroundImage.mimeType}
                        />
                      </video>
                    )}
                  </div>
                  <div className="block md:hidden absolute inset-0 w-full h-full overflow-hidden border-inherit">
                    {slider.backgroundImageMobile ? (
                      slider.backgroundImageMobile.mimeType.includes(
                        "image"
                      ) ? (
                        <GatsbyImage
                          className="max-w-full w-full h-[585px] md:h-full align-bottom object-cover"
                          image={slider.backgroundImageMobile.gatsbyImageData}
                          alt={slider.backgroundImageMobile.title}
                          objectFit="cover"
                          objectPosition="center"
                          loading="eager"
                          quality={100}
                          webpOptions={{
                            lossless: true
                          }}
                        />
                      ) : (
                        <video
                          title={slider.backgroundImage.title}
                          src={slider.backgroundImage.file.url}
                          autoPlay
                          loop
                          muted
                          controlsList="nodownload"
                          className="w-full h-full object-cover"
                        >
                          <source
                            src={slider.backgroundImage.file.url}
                            type={slider.backgroundImage.mimeType}
                          />
                        </video>
                      )
                    ) : slider.backgroundImage.mimeType.includes("image") ? (
                      <GatsbyImage
                        className="max-w-full w-full h-[585px] md:h-full align-bottom object-cover"
                        image={slider.backgroundImage.gatsbyImageData}
                        alt={slider.backgroundImage.title}
                        objectFit="cover"
                        objectPosition="center"
                        loading="eager"
                        quality={100}
                        webpOptions={{
                          lossless: true
                        }}
                      />
                    ) : (
                      <video
                        title={slider.backgroundImage.title}
                        src={slider.backgroundImage.file.url}
                        autoPlay
                        loop
                        muted
                        controlsList="nodownload"
                        className="w-full h-full object-cover"
                      >
                        <source
                          src={slider.backgroundImage.file.url}
                          type={slider.backgroundImage.mimeType}
                        />
                      </video>
                    )}
                  </div>
                  <div className="relative w-full flex items-end justify-center text-center pt-[90px] px-5 pb-5 border-inherit z-[2]">
                    <div className="relative flex flex-col items-center justify-end max-w-[1200px] w-full lg:px-[60px] py-16 border-inherit">
                      {slider.caption && (
                        <strong className="text-center text-white font-bold px-4 rounded-full pt-1 bg-secondary-700 text-lg md:text-xl leading-5 tracking-wide uppercase mb-2 h-8 flex items-center">
                          {slider.caption}
                        </strong>
                      )}
                      <h1 className="text-[26px] sm:text-[40px] md:text-[62px] tracking-[0.4px] leading-[40px] sm:leading-[60px] md:leading-[70px] font-bold">
                        {slider.title}
                      </h1>
                      {slider?.description?.description && (
                        <p className="text-sm md:text-base tracking-[0.4px] max-w-[550px] mt-[10px] h-11 md:h-12 line-clamp-2">
                          {slider.description.description}
                        </p>
                      )}
                      {slider.buttonText && (
                        <div className="flex items-center mt-1 sm:mt-3">
                          <button
                            onClick={() => navigate(slider.buttonLink)}
                            className="px-[35px] py-3 cursor-pointer rounded-full no-underline text-[#1f1f1f] h-14 lg:mx-auto text-[15px] font-bold tracking-[1px] leading-7 box-border mt-5 z-[3] hover:scale-105 transform transition-all duration-200 bg-white flex items-center justify-center"
                          >
                            {slider.buttonText}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-[1] pb-[30px] w-fit">
        <DotControls
          arr={data}
          activeIndex={selectedIndex}
          animationDuration="500ms"
          color="white"
          onClick={(e) => handleGoTo(e.clickedIndex)}
        />
      </div>
      {/* <div className="absolute flex flex-col items-center -bottom-[30px] left-0 w-full z-[2]">
        <h5 className="text-white text-base tracking-[0.4px] leading-7 font-normal">
          Explore More
        </h5>
        <button
          onClick={() => navigateScroll("categories")}
          title="Scroll Down"
          className="inline-flex justify-center items-center mt-[15px] py-[23px] px-[15px] bg-white rounded-full cursor-pointer
          border-2 border-solid border-white text-black transition-all duration-200 ease-linear shadow-lg hover:scale-105"
        >
          <SvgArrowDown style={{ rotate: 45, cursor: "pointer" }} />
        </button>
      </div> */}
    </div>
  );
};

export default memo(Hero);
